import { motion } from "framer-motion";
import * as LucideIcons from "lucide-react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import type { Assistant } from "@/lib/index";
import { hoverLift } from "@/lib/motion";

interface AssistantCardProps {
  assistant: Assistant;
  compact?: boolean;
}

const categoryColors = {
  "Learn & Grow": "bg-accent/10 text-accent border-accent/20",
  "Money & Work": "bg-primary/10 text-primary border-primary/20",
  "Home & Support": "bg-accent/10 text-accent border-accent/20",
};

export default function AssistantCard({ assistant, compact = false }: AssistantCardProps) {
  const LucideMap = LucideIcons as unknown as Record<string, React.ComponentType<{ className?: string }>>;
  const IconComponent = LucideMap[assistant.icon];

  return (
    <motion.div
      variants={hoverLift}
      initial="rest"
      whileHover="hover"
      className="h-full"
    >
      <Card className="h-full p-6 border-2 border-border hover:border-accent/40 transition-colors duration-200 bg-card">
        <div className="flex flex-col h-full gap-4">
          <div className="flex items-start justify-between gap-3">
            <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-accent/10 flex items-center justify-center">
              {IconComponent ? (
                <IconComponent className="w-6 h-6 text-accent" />
              ) : (
                <span className="text-2xl">{assistant.icon}</span>
              )}
            </div>
            <Badge
              variant="outline"
              className={`${categoryColors[assistant.category]} text-xs font-medium px-2 py-1`}
            >
              {assistant.category}
            </Badge>
          </div>

          <div className="flex-1 flex flex-col gap-2">
            <h3 className="text-lg font-semibold text-foreground">
              {assistant.name}
            </h3>
            <p className="text-sm text-muted-foreground leading-relaxed">
              {assistant.description}
            </p>
          </div>

          {!compact && (
            <div className="pt-3 border-t border-border/50">
              <div className="flex items-start gap-2">
                <div className="flex-shrink-0 w-1 h-full bg-accent/30 rounded-full mt-1" />
                <p className="text-xs text-muted-foreground italic leading-relaxed">
                  "{assistant.examplePrompt}"
                </p>
              </div>
            </div>
          )}
        </div>
      </Card>
    </motion.div>
  );
}